import React, { useEffect, useState } from 'react';
import P, { InferProps } from 'prop-types';
import { useCollection, useDocumentOnce } from 'react-firebase-hooks/firestore';
import get from 'lodash/get';
import ReactDiffViewer from 'react-diff-viewer-continued';
import stringify from 'json-stable-stringify';
import ListItemLink from './list-item-link';
import firebaseApp from '../../../firebase-app';
import BreadCrumbs from './breadcrumbs';
import { Grid } from '@mui/material';
import SubCollections from './sub-collections';
import { useStringsContext } from '../../../contexts/strings';
import { httpsCallable } from 'firebase/functions';
import { collection, doc } from 'firebase/firestore';
import Paper from '@mui/material/Paper';

const LOADING = 'LOADING';

function TimeMachineDoc({ path }: InferProps<typeof TimeMachineDoc.propTypes>) {
  const [selectedRightDocPath, setSelectedRightDocPath] = useState(path || null);
  const [rightDoc, rightDocLoading, rightDocError] = useDocumentOnce(
    selectedRightDocPath ? doc(firebaseApp.db, selectedRightDocPath) : null
  );
  const [subCollections, setSubCollections] = useState<any>(LOADING);
  const [history, historyLoading] = useCollection(
    path ? collection(firebaseApp.db, `${path}/history`) : null
  );
  const [selectedLeftDocPath, setSelectedLeftDocPath] = useState<string | null>(null);
  const [historyDoc] = useDocumentOnce(
    selectedLeftDocPath ? doc(firebaseApp.db, selectedLeftDocPath) : null
  );
  const [oldValue, setOldValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const { strings } = useStringsContext();

  useEffect(() => {
    if (historyDoc) {
      setOldValue(stringify(historyDoc?.data(), { space: '  ' }) || '');
    } else {
      setOldValue('');
    }
  }, [historyDoc]);

  useEffect(() => {
    if (rightDoc) {
      setNewValue(stringify(rightDoc?.data(), { space: '  ' }) || '');
    } else {
      setNewValue('');
    }
  }, [rightDoc]);

  useEffect(() => {
    setSelectedLeftDocPath(null);
    setSelectedRightDocPath(path);
    async function load() {
      try {
        setSubCollections(LOADING);
        const subCollections = await httpsCallable(
          firebaseApp.functions,
          'getSubCollections'
        )({ path: path || '/' });
        setSubCollections(subCollections.data);
      } catch (e: any) {
        console.error(e.message);
      }
    }
    load();
  }, [path]);

  const getDocPathFromId = (id: string) => `${path}/history/${id}`;

  const onLeftHistorySelect = (event: React.FormEvent<HTMLSelectElement>) => {
    if (event.currentTarget.value === '0') {
      setSelectedLeftDocPath(null);
    } else {
      setSelectedLeftDocPath(getDocPathFromId(event.currentTarget.value));
    }
  };

  const onRightHistorySelect = (event: React.FormEvent<HTMLSelectElement>) => {
    if (event.currentTarget.value === '0') {
      setSelectedRightDocPath(null);
    } else if (event.currentTarget.value === 'CURRENT') {
      setSelectedRightDocPath(path);
    } else {
      setSelectedRightDocPath(getDocPathFromId(event.currentTarget.value));
    }
  };

  if (rightDocLoading) {
    return <p>{strings.messages.loading}</p>;
  }

  if (rightDocError) {
    return <p>{rightDocError.message}</p>;
  }

  const renderSubCollections = () => {
    const render = () => {
      const nonHistoryCols = get(subCollections, 'collections', []).filter(
        (s: string) => s !== 'history'
      );

      if (nonHistoryCols && nonHistoryCols.length) {
        return (
          <SubCollections>
            {nonHistoryCols.map((s: string) => {
              const subCollectionPath = `${path}/${s}`;
              const subCollectionPathEncoded = encodeURIComponent(subCollectionPath);
              return (
                <ListItemLink
                  key={subCollectionPathEncoded}
                  to={`/time-machine/${subCollectionPathEncoded}`}
                  primary={s}
                />
              );
            })}
          </SubCollections>
        );
      }

      return null;
    };

    return render();
  };

  const renderHistory = () => {
    if (historyLoading) {
      return <p>{strings.messages.loading}</p>;
    }

    if (history && history.docs.length) {
      return (
        <div>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <select onChange={onLeftHistorySelect} defaultValue="0">
                <option key="0" value="0">
                  {strings.timeMachine.pickHistory}
                </option>
                {history
                  ? history.docs.map((h: any) => (
                      <option
                        key={h.id}
                        selected={getDocPathFromId(h.id) === selectedLeftDocPath ? true : false}
                      >
                        {h.id}
                      </option>
                    ))
                  : null}
              </select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <select onChange={onRightHistorySelect}>
                <option
                  key="CURRENT"
                  value={'CURRENT'}
                  selected={path === selectedRightDocPath ? true : false}
                >
                  Current
                </option>
                {history
                  ? history.docs.map((h: any) => (
                      <option
                        key={h.id}
                        selected={getDocPathFromId(h.id) === selectedRightDocPath ? true : false}
                      >
                        {h.id}
                      </option>
                    ))
                  : null}
              </select>
            </Grid>
          </Grid>

          <ReactDiffViewer
            oldValue={oldValue}
            newValue={newValue}
            splitView={true}
            hideLineNumbers
            leftTitle={historyDoc?.ref.path}
            rightTitle={rightDoc?.ref.path}
          />
        </div>
      );
    }

    if (newValue) {
      return (
        <div>
          <p>{strings.timeMachine.noHistoryForDocument}</p>
          <ReactDiffViewer
            oldValue={''}
            newValue={newValue}
            splitView={false}
            hideLineNumbers
            leftTitle={historyDoc?.ref.path}
            rightTitle={rightDoc?.ref.path}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <BreadCrumbs path={path} />

      <Paper>
        {renderSubCollections()}
        {renderHistory()}
      </Paper>
    </>
  );
}

TimeMachineDoc.propTypes = {
  path: P.string.isRequired,
};

export default TimeMachineDoc;
